import mitt from 'mitt';
import { getCurrentInstance, onBeforeUnmount } from 'vue';

// Define all event types for type safety
export type BusEvents = {
  // Modal events (TODO: Define proper payload types)
  'modal:confirm'?: { title: string; message?: string; okHandler: () => void; cancelHandler?: () => void };
  'modal:info'?: { title: string; message?: string; size?: 'sm' | 'md' | 'lg' };

  // Overlay events
  'overlay-on': void;
  'overlay-off': void;
  'overlay-click': void;

  // Header dropdown
  'headerDropdown:show': void;
  'headerDropdown:hide': void;

  // Sidebar
  'sidebar:uncollapsed': void;

  // Payment
  'payment-info-updated': void;

  // List operations
  'item-deleted': { id: number };

  // Stats
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  'stats-update': any; // Can be boolean, object with filters, or void
  'sub-report-loaded': void;
  'sub-data-loaded': void; // TODO: Define proper payload type
  'update-stats-table-cells': void;

  // Campaign events
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  'campaign:target-changed': any; // Target form data - can be any shape
  'campaign:target-form-ready': void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  'campaign:update-spots': any; // Spots data with include/exclude
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  'add-campaign-check-category': any; // TODO: Define proper payload type
  'not-auto-show-target-form': void; // TODO: Define proper payload type

  // Zones and spots events
  'clean-zones-list': void; // TODO: Define proper payload type
  'update-zones-list': unknown; // Legacy event, loosely typed
  'update-spots': unknown; // Legacy event, loosely typed
  'update-spots-and-domains': void; // TODO: Define proper payload type
  'update-domains': unknown; // Legacy event, loosely typed
  'update-marketplace-list': void; // TODO: Define proper payload type

  // Load more
  'load-more-loaded': void; // TODO: Define proper payload type

  // Mobile menu
  'mobile-menu:close-others': string; // Menu ID of the menu being opened

  // Page events
  'page-after-enter': void; // TODO: Define proper payload type

  // Datepicker
  'reset-datepicker': void; // TODO: Define proper payload type

  // Role switching
  'switch-role:end': void; // TODO: Define proper payload type
  'switch-role:start': void; // TODO: Define proper payload type

  // Table
  'table-cols-minimize': boolean;

  // SQL queries
  'update-sql-queries': { sql: Array<string>; loading: boolean };

  // Publisher/Target tree loading
  'tree-load-regions': { dict: string; values: Array<number> };
  'tree-load-os-versions': { dict: string; values: Array<number> };

  // Marketplace
  'marketplace:update-selected': void;

  // Stats/Filters
  'submit-filters': void;
  'dimension-menu-changed': void;

  // Balance
  'balance-loading': boolean;

  // Test-only events (used in event bus integration tests)
  'test-event': unknown;
  'cycle-test': unknown;
  'multi-listener-test': unknown;
};

// Create typed event emitter
const bus = mitt<BusEvents>();

// Dev-mode logging for debugging
if (process.env.NODE_ENV !== 'production') {
  const originalEmit = bus.emit;
  bus.emit = ((event, payload) => {
    console.debug('[Bus] emit:', event, payload);
    return originalEmit(event, payload);
  }) as typeof bus.emit;
}

/**
 * Composable for automatic event cleanup on component unmount.
 * Prevents memory leaks by tracking listeners and cleaning them up.
 * Works with both Options API and Composition API.
 *
 * WARNING: Auto-cleanup only works inside Vue component context (setup/lifecycle hooks).
 * If called outside component context (Vuex stores, utilities, plugins), you MUST manually call cleanup().
 *
 * @example
 * // Inside component - auto-cleanup on unmount
 * created() {
 *   const { on, emit } = useEventBus();
 *   on('item-deleted', this.handleDelete);
 * }
 *
 * @example
 * // Outside component - manual cleanup required
 * const { on, cleanup } = useEventBus();
 * on('item-deleted', handler);
 * // Later: must call cleanup() manually when done
 * cleanup();
 *
 * @example
 * // Composition API / <script setup>
 * const { on, emit } = useEventBus();
 * on('item-deleted', handleDelete);  // Auto-cleanup on unmount
 */
export const useEventBus = () => {
  const listeners = new Set<() => void>();

  const on = <K extends keyof BusEvents>(event: K, handler: (payload: BusEvents[K]) => void) => {
    bus.on(event, handler);
    const cleanup = () => bus.off(event, handler);
    listeners.add(cleanup);
    return cleanup;
  };

  const once = <K extends keyof BusEvents>(event: K, handler: (payload: BusEvents[K]) => void) => {
    const wrapper = (payload: BusEvents[K]) => {
      bus.off(event, wrapper);
      handler(payload);
    };
    bus.on(event, wrapper);
    const cleanup = () => bus.off(event, wrapper);
    listeners.add(cleanup); // Auto-cleanup if component unmounts before event fires!
    return cleanup;
  };

  const cleanup = () => {
    listeners.forEach((fn) => fn());
    listeners.clear();
  };

  // Auto-cleanup if in component context
  if (getCurrentInstance()) {
    onBeforeUnmount(cleanup);
  }

  return {
    on,
    once, // Now with auto-cleanup
    emit: bus.emit,
    cleanup,
    off: bus.off, // For manual cleanup if needed
  };
};

// Backward compatibility adapter for gradual migration
// Allows old $bus.$on/$emit/$off syntax to work alongside new API
const busAdapter = {
  // New API (mitt)
  on: bus.on.bind(bus),
  off: bus.off.bind(bus),
  emit: bus.emit.bind(bus),
  all: bus.all, // For testing - access to listener map

  // Backward compatibility (Vue 2 event bus API)
  $on: bus.on.bind(bus),
  $off: bus.off.bind(bus),
  $emit: bus.emit.bind(bus),
};

export default busAdapter;
